import type { DataGridRef, DialogRef, DynamicFormField, OdinIconName } from '@myosh/odin-components';
import { IconButton, OdinIconSize, Tooltip, useTestTag } from '@myosh/odin-components';
import clsx from 'clsx';
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Subject, takeUntil } from 'rxjs';
import type { ExportFileFormat } from '../../@types/records';
import useAddonQuery from '../../hooks/use-addon-query';
import { useOnlineStatus } from '../../hooks/use-online-status';
import { useAppDispatch } from '../../redux/hooks';
import { useDeleteRecordMutation, useGetRecordVersionsQuery } from '../../redux/services/record';
import { useGetFormAddOnsQuery } from '../../redux/services/view';
import { submitExportSingleRecordJob } from '../../redux/slices/background-job-slice';
import { getDataGridReference } from '../../services/data-grid.service';
import { dismiss, showInfo, showSuccess } from '../../services/notification.service';
import { dataTag } from '../../utils/data-tags';
import type { ActiveRecordContextProps } from '../active-record/active-record.component';
import ConfirmationDialog from '../common/confirmation-dialog.component';
import AuditView from './audit-view.component';
import { QrCodeShare } from './qr-code-share.component';
import RecordPdfExportModal from './record-pdf-export-modal';

interface RecordMenuProps {
  panelContextProps?: ActiveRecordContextProps;
  recordId?: string;
  recordTitle?: string;
  hasDeletePermission: boolean;
  isRecordReadOnly: boolean;
  onClose: (id: string) => void;
  onRefreshRecordClick?: () => void;
  hasViewRecordAuditPermission: boolean;
  formId: number;
  vikingLinkUrl?: string;
  formGroups?: DynamicFormField[];
}
export interface MenuComponentProps {
  title: string;
  menuElement?: (children: ReactNode) => ReactElement;
  className: string;
  onClick?: () => void;
  icon?: OdinIconName;
  hidden?: boolean;
  subMenuItems?: Array<SubMenuItem>;
  isSeparator?: boolean;
  titleElement?: ReactElement;
}

interface MenuItemProps {
  menu: MenuComponentProps;
  fullScreenMode?: boolean;
}

interface SubmenuItemsProps {
  items: SubMenuItem[];
  subMenuVisibility: boolean;
  fullScreenMode?: boolean;
}
interface SubMenuItem {
  title: string;
  onClick: () => void;
}

export function RecordMenu({
  recordId,
  recordTitle,
  panelContextProps,
  hasDeletePermission: canDelete,
  hasViewRecordAuditPermission: canViewRecordAudit,
  onClose,
  onRefreshRecordClick,
  isRecordReadOnly,
  formId,
  vikingLinkUrl,
  formGroups,
}: RecordMenuProps) {
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [fullScreenMode, setFullScreenMode] = useState<boolean>();
  const [isRecordPdfExportModalVisible, setIsRecordPdfExportModalVisible] = useState(false);

  const deleteModalReference = useRef<DialogRef>(null);
  const auditModalReference = useRef<DialogRef>(null);
  const destroySubject = useRef(new Subject<void>());
  const dataGridRef = useRef<DataGridRef | undefined>(undefined);
  const toastId = useRef<string | undefined>(undefined);

  const params = useAddonQuery(recordId);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isOnline = useOnlineStatus();

  const [deleteRecord] = useDeleteRecordMutation();

  const { data: formAddons } = useGetFormAddOnsQuery(formId, {
    skip: formId === undefined,
  });

  const { data: recordVersions } = useGetRecordVersionsQuery(
    { recordId: recordId ? Number(recordId) : 0 },
    { skip: !recordId }
  );
  const activeVersionId = recordVersions && recordVersions[0]?.id;

  useEffect(() => {
    setFullScreenMode(panelContextProps?.fullScreen);
  }, [panelContextProps]);

  useEffect(() => {
    getDataGridReference()
      .pipe(takeUntil(destroySubject.current))
      .subscribe((gridRef) => {
        if (gridRef) {
          dataGridRef.current = gridRef;
        }
      });

    return () => {
      destroySubject.current.next();
      destroySubject.current.complete();
    };
  }, []);

  const tagCreator = useTestTag('record-menu');

  const onDeleteRecord = () => {
    if (recordId) {
      const selectedRecordId = parseInt(recordId);

      toastId.current = showInfo(t('performing-delete-message'));
      deleteRecord(selectedRecordId)
        .unwrap()
        .then(() => {
          showSuccess(t('delete-successful-message'));
          dataGridRef.current?.api.data.getDataOverwritePageCache();
          onClose(recordId);
        })
        .finally(() => dismiss(toastId.current));
    }
  };

  const onAuditButtonClicked = () => {
    auditModalReference.current?.show();
  };

  const onShowShareMenu = () => {
    setShowShareMenu(true);
  };

  const onHideShareMenu = () => {
    setShowShareMenu(false);
  };

  const onRefreshClick = () => {
    onRefreshRecordClick?.();
  };

  const handleRecordExport = (format: ExportFileFormat) => {
    if (!recordId || !recordTitle) return;
    dispatch(submitExportSingleRecordJob({ formName: recordTitle, format, recordId }));
  };

  const onHideExportPdfModal = () => setIsRecordPdfExportModalVisible(false);

  const actionDropdownItemStyle = clsx('text-sm flex items-center whitespace-nowrap  leading-5  cursor-pointer', {
    'py-1 px-2 hover:bg-primary-4': !fullScreenMode,
  });
  const addOnMenuItems = useMemo(() => {
    const addOns: Array<MenuComponentProps> | undefined =
      params && formAddons
        ? formAddons
            .filter((addOn) => addOn.result.displayType === 'RECORD')
            .map((addOnWrapper) => {
              const addOn = addOnWrapper.result;
              const url = addOn.url + params;
              const link = (children: ReactNode) => (
                <a href={url} key={addOn.caption} rel="noreferrer" target="_blank" className={actionDropdownItemStyle}>
                  {children}
                </a>
              );
              return {
                title: addOn.caption,
                //Hardcoded default icon until we get correct icons from the backend
                icon: addOn.icon ?? 'ExternalLink',
                menuElement: link,
              } as MenuComponentProps;
            })
        : undefined;

    return addOns;
  }, [formAddons, fullScreenMode, recordId, params, actionDropdownItemStyle]);

  const separator = useMemo(() => {
    if (addOnMenuItems && addOnMenuItems.length > 0) {
      const props: MenuComponentProps = { title: 'separator', className: actionDropdownItemStyle, isSeparator: true };
      return <MenuItem menu={props} fullScreenMode={fullScreenMode} />;
    }
    return undefined;
  }, [addOnMenuItems]);

  const vikingTitleElement = useCallback(
    (children: ReactNode): ReactElement => {
      return (
        <VikingMenuElement vikingLinkUrl={vikingLinkUrl} className={actionDropdownItemStyle}>
          {children}
        </VikingMenuElement>
      );
    },
    [vikingLinkUrl, fullScreenMode]
  );

  const menus: MenuComponentProps[] = [
    {
      title: t('audit-log'),
      className: actionDropdownItemStyle,
      onClick: onAuditButtonClicked,
      icon: 'Clock',
      hidden: !canViewRecordAudit,
    },
    {
      title: t('delete'),
      className: actionDropdownItemStyle,
      onClick: () => deleteModalReference.current?.show(),
      icon: 'Trash',
      hidden: !isOnline || !canDelete || (activeVersionId ? activeVersionId !== Number(recordId) : false),
    },
    {
      title: t('export'),
      className: actionDropdownItemStyle,
      icon: 'Download',
      subMenuItems: [
        {
          title: 'CSV',
          onClick: () => handleRecordExport('CSV'),
        },
        {
          title: 'Excel',
          onClick: () => handleRecordExport('EXCEL'),
        },
        {
          title: 'PDF',
          onClick: () => setIsRecordPdfExportModalVisible(true),
        },
      ],
    },
    {
      title: t('refresh'),
      className: actionDropdownItemStyle,
      onClick: onRefreshClick,
      icon: 'RefreshCcw',
      hidden: !isRecordReadOnly,
    },
    {
      title: t('share'),
      className: actionDropdownItemStyle,
      onClick: onShowShareMenu,
      icon: 'Share',
    },
    {
      title: t('viking'),
      className: actionDropdownItemStyle,
      menuElement: vikingTitleElement,
      icon: 'ExternalLink',
      hidden: !vikingLinkUrl,
    },
  ];

  const menuSyle = clsx({
    'z-30 absolute right-0 top-6 hidden w-48 flex-col bg-mono-1 py-1 shadow group-hover:flex': !fullScreenMode,
    'flex items-center gap-2': fullScreenMode,
  });

  return (
    <>
      <div className={menuSyle} ref={tagCreator()}>
        {menus.map((menu) => {
          if (menu.hidden) {
            return;
          }

          return <MenuItem key={menu.title} menu={menu} fullScreenMode={fullScreenMode} />;
        })}
        {separator}
        {addOnMenuItems?.map((addOn) => {
          return <MenuItem key={addOn.title} menu={addOn} fullScreenMode={fullScreenMode} />;
        })}
      </div>

      <AuditView ref={auditModalReference} header={`${t('audit-log')} - ` + recordTitle} recordId={recordId} />
      {showShareMenu && <QrCodeShare isDialogVisible={showShareMenu} onDialogClose={onHideShareMenu} />}
      <ConfirmationDialog
        header={t('delete-modal-header')}
        confirmButtonText={t('delete')}
        ref={deleteModalReference}
        onConfirm={onDeleteRecord}
        hasCancelButton
      >
        <p>
          {recordVersions && recordVersions.length > 1
            ? t('delete-record-multiple-versions-warning')
            : t('record-delete-message')}
        </p>
      </ConfirmationDialog>
      <RecordPdfExportModal
        isVisible={isRecordPdfExportModalVisible}
        recordId={recordId}
        recordTitle={recordTitle}
        hide={onHideExportPdfModal}
        formGroups={formGroups}
      />
    </>
  );
}

export const MenuItem = ({ menu, fullScreenMode }: MenuItemProps) => {
  const [subMenuVisibility, setSubMenuVisibility] = useState(false);

  const tagCreator = useTestTag(menu.title || '');

  const menuItemStyle = clsx({
    'hover:text-primary-2': fullScreenMode,
  });

  if (menu.isSeparator) {
    return !fullScreenMode ? <hr key={menu.title} className="m-2" /> : null;
  }

  const getMenuItemContent = () => {
    const tooltip = menu.icon && (
      <Tooltip tooltipClassName="bg-mono-1" description={menu.title} debounceTime={100} disabled={!fullScreenMode}>
        <IconButton
          icon={menu.icon}
          iconSize={fullScreenMode ? OdinIconSize.Small : OdinIconSize.ExtraSmall}
          classNames={menuItemStyle}
        />
      </Tooltip>
    );
    const title = !fullScreenMode && (menu.titleElement ?? menu.title);
    const submenus = menu?.subMenuItems && (
      <SubmenuItems items={menu.subMenuItems} subMenuVisibility={subMenuVisibility} fullScreenMode={fullScreenMode} />
    );

    return (
      <div className="flex items-center gap-2">
        {tooltip}
        {title}
        {submenus}
      </div>
    );
  };

  const customElement: ReactElement | undefined = menu.menuElement ? menu.menuElement(getMenuItemContent()) : undefined;
  return (
    customElement ?? (
      <div
        key={menu.title}
        className={`${menu.className} ${subMenuVisibility ? 'group relative' : ''}`}
        onClick={menu?.onClick}
        onMouseEnter={() => menu?.subMenuItems && setSubMenuVisibility(true)}
        onMouseLeave={() => menu?.subMenuItems && setSubMenuVisibility(false)}
        ref={tagCreator('menu-item')}
      >
        {getMenuItemContent()}
      </div>
    )
  );
};

const SubmenuItems = ({ items, subMenuVisibility, fullScreenMode }: SubmenuItemsProps) => {
  const submenuItemsStyle = clsx({
    'z-20 absolute -top-1 right-48 w-max flex-col bg-mono-1 py-1  shadow': !fullScreenMode,
    hidden: !subMenuVisibility,
    'z-30 absolute right-0 top-6 w-48 flex-col bg-mono-1 py-1  shadow ': fullScreenMode,
  });

  const tagCreator = useTestTag('sub-menu');

  return (
    <div className={submenuItemsStyle} ref={tagCreator()}>
      {items.map((item) => {
        return (
          <div
            key={item.title}
            className="flex cursor-pointer items-center whitespace-nowrap px-2 py-1 text-sm leading-5 hover:bg-primary-4"
            onClick={() => item.onClick()}
            ref={tagCreator(`${dataTag(item.title)}-menu-item`)}
          >
            {item.title}
          </div>
        );
      })}
    </div>
  );
};

interface VikingMenuElementType {
  children: ReactNode;
  vikingLinkUrl?: string;
  className?: string;
}
const VikingMenuElement = ({ children, vikingLinkUrl, className }: VikingMenuElementType) => {
  const testTag = useTestTag('viking');
  return vikingLinkUrl ? (
    <a
      href={vikingLinkUrl}
      target="_blank"
      rel="noreferrer"
      className={className}
      ref={testTag('menu-item')}
      key="viking"
    >
      {children}
    </a>
  ) : (
    <></>
  );
};
